import {
	Component,
	ElementRef,
	Inject,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	SimpleChanges,
	ViewChild
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommentFilterService } from '@go-modules/services/comment-filter/comment-filter.service';
import { commentModelToken, CommentModel } from 'go-modules/models/comment/comment.factory';
import { CommentListService, ProcessedComment } from '@go-modules/services/comment-list/comment-list.service';
import { NgxFeatureFlagService } from '@go-modules/services/feature-flag/feature-flag.service';

const CAPTION_DURATION = 4000;

@Component({
	selector: 'ngx-comment-captions',
	templateUrl: './comment-captions.component.html',
	styleUrls: ['./comment-captions.component.scss'],
	standalone: false
})
export class NgxCommentCaptionsComponent implements OnInit, OnDestroy, OnChanges {
	@Input() public disabled: boolean = false;
	@Input() public player: any;
	@ViewChild('captionContainerElRef', { static: true }) public captionContainerElRef!: ElementRef;

	public collection: ProcessedComment[] = [];
	public feedbackItem: ProcessedComment | null = null;
	private lookup: { [key: number]: ProcessedComment } = {};
	private lastCommentCaption: ProcessedComment | null = null;
	private readonly componentDestroyed$ = new Subject<void>();

	constructor (
		private ngxCommentFilterService: CommentFilterService,
		private commentListService: CommentListService,
		public featureFlagService: NgxFeatureFlagService,
		@Inject(commentModelToken) private Comment: ReturnType<typeof CommentModel>
	) {}

	public ngOnInit (): void {
		this.toggleListeners(!this.disabled);

		this.commentListService.comments$
			.pipe(takeUntil(this.componentDestroyed$))
			.subscribe((comments) => {
				this.clearCollection();
				comments.forEach((comment) => this.addToCollection(comment));
				this.updateLookup();
				this.validate();
			});
	}

	public ngOnDestroy (): void {
		this.toggleListeners(false);
		this.componentDestroyed$.next();
		this.componentDestroyed$.complete();
	}

	public ngOnChanges (changes: SimpleChanges) {
		if (changes.disabled.previousValue !== changes.disabled.currentValue) {
			this.toggleListeners(!changes.disabled.currentValue);
		}
	}

	public isContentOverflowing (): boolean {
		const el = this.captionContainerElRef.nativeElement as HTMLElement;
		return el.clientHeight < el.scrollHeight;
	}

	private toggleListeners (enable: boolean): void {
		if (enable) {
			this.player?.on('time', this.onTime);
			this.player?.on('complete', this.onComplete);
		} else {
			this.player?.off('time', this.onTime);
			this.player?.off('complete', this.onComplete);
		}

		this.validate();
	}

	private onTime = (): void => this.validate();
	private onComplete = (): void => this.validate();

	private validate (): void {
		const time = !this.disabled ? this.player.getTime() : null;
		this.render(time);
	}

	private render (time: number | null): void {
		time = time ? Math.floor(time / 1000) : null;
		const feedbackItem: ProcessedComment = this.lookup[time ?? -1];

		if (feedbackItem === this.lastCommentCaption) return;

		this.feedbackItem = null;

		if (
			feedbackItem?.created_by &&
			!this.ngxCommentFilterService.shouldCommentShow(feedbackItem)
		) {
			return;
		}

		if (feedbackItem) {
			this.feedbackItem = feedbackItem;
		}

		this.lastCommentCaption = feedbackItem;
	}

	private updateLookup (): void {
		this.lookup = {};
		this.collection.forEach((feedbackItem) => this.updateLookupItem(feedbackItem));
	}

	private updateLookupItem (feedbackItem: ProcessedComment): void {
		feedbackItem = this.Comment.newInstance(feedbackItem);
		const { time } = feedbackItem;
		const startTime = Math.floor((time as number) / 1000);
		this.lookup[startTime] = feedbackItem;

		const ccEnd = (time as number) + CAPTION_DURATION;
		let duration = Math.floor((ccEnd - (time as number)) / 1000);
		while (duration--) {
			this.lookup[startTime + duration + 1] = feedbackItem;
		}
	}

	private addToCollection (feedbackItem: ProcessedComment): void {
		if (
			feedbackItem.time === null ||
			(!feedbackItem.text && !feedbackItem.markers.length && !feedbackItem.media.length)
		) {
			return;
		}

		this.collection.push(feedbackItem);
	}

	private clearCollection (): void {
		this.collection = [];
	}
}
